import { useCallback, useEffect, useMemo, useState } from "react";
import FieldLayout, { FieldErrors } from "components/field/FieldLayout";
import RadarBurstPanel from "components/field/RadarBurstPanel";
import RadarCommissioning from "components/field/RadarCommissioning";
import RadarExplainers from "components/field/RadarExplainers";
import RadarHistory from "components/field/RadarHistory";
import RadarPanel from "components/field/RadarPanel";
import RadarPhase from "components/field/RadarPhase";
import RadarRain from "components/field/RadarRain";
import RadarStreamPanel from "components/field/RadarStreamPanel";
import RadarSummary from "components/field/RadarSummary";
import { useFieldNodes, useFieldResource } from "hooks/useFieldNodes";
import { useRadarAnalysis, useRadarRain } from "hooks/useRadarData";
import { rtctApi } from "services/rtctApi";
import { needsFastRefresh } from "utils/fieldFormat";
import { onCellular, timeOf } from "utils/radarDiagnostics";
import { uploadCadence } from "utils/radarBurst";
import { rainDays } from "utils/radarRain";

// Radar analysis: the radar's own reading beside our estimate, rain at the
// node, the rich data behind both (rich readings on every wake, bursts on
// upload wakes, the live stream), micro-movement from the phase of the beat
// signals, the tools that probe the radar itself and the empty-pipe profile
// our estimate learns from. Node Control keeps the settings; this page shows
// what they bring.
export default function RadarAnalysis() {
  const nodes = useFieldNodes();
  return <RadarAnalysisContent key={nodes.mac} nodes={nodes} />;
}

function RadarAnalysisContent({ nodes }) {
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setRefresh((n) => n + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);
  const telemetry = useFieldResource(nodes.mac, rtctApi.telemetry, refresh),
    settings = useFieldResource(nodes.mac, rtctApi.settings, refresh);
  // One window for the timeline and the burst history: 24 h, 7 or 30 days.
  const [minutes, setMinutes] = useState(1440);
  const [learned, setLearned] = useState(0);
  const analysis = useRadarAnalysis(
    nodes.mac,
    Math.max(1, Math.round(minutes / 1440)),
    refresh + learned,
  );
  // Rain at the node over the same window, its events and the rain gauge's
  // rain marked on the timeline too.
  const rain = useRadarRain(nodes.mac, rainDays(minutes), refresh);
  // Firmware .62: the rich wakes the node held back have no burst of their
  // own; the burst list shows them in their hour from the series (every one
  // unless the series is thinned out).
  const held = useMemo(
    () => analysis.points.filter((p) => p.heldBack),
    [analysis.points],
  );
  const [newest, setNewest] = useState(null);
  const [focus, setFocus] = useState(null);
  const pick = useCallback(
    (id) => setFocus((old) => ({ id, n: (old?.n ?? 0) + 1 })),
    [],
  );
  // A command sent from the tools refreshes its request status sooner.
  useEffect(() => {
    if (!needsFastRefresh(settings.data)) return;
    const timer = setTimeout(() => setRefresh((n) => n + 1), 15000);
    return () => clearTimeout(timer);
  }, [settings.data]);
  // A link to a part of this page (Sensor settings links to the tools).
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView?.({ block: "start" });
  }, []);
  const samples = telemetry.data?.samples || [];
  // How often the node uploads: rich readings come with its uploads, so the
  // newest can be that old.
  const reported = settings.data?.reported;
  const uploads = useMemo(() => {
    const list = telemetry.data?.samples || [];
    const newest = list.reduce(
      (best, x) =>
        (timeOf(x?.timestamp) ?? -Infinity) >
        (timeOf(best?.timestamp) ?? -Infinity)
          ? x
          : best,
      null,
    );
    return uploadCadence(reported || {}, list, {
      cellular: onCellular(newest?.raw),
    }).seconds;
  }, [reported, telemetry.data]);

  return (
    <FieldLayout
      nodes={nodes}
      section="analysis"
      title="Radar analysis"
      subtitle="The radar's reading, our estimate and the raw data."
      refresh={() => setRefresh((n) => n + 1)}
    >
      <FieldErrors errors={[settings.error, telemetry.error]} />
      <RadarSummary
        newest={newest}
        analysis={analysis}
        minutes={minutes}
        onMinutes={setMinutes}
        onPick={pick}
        uploadSeconds={uploads}
        rain={rain.rain?.events}
        gauge={rain.rain?.gauge}
      />
      <RadarRain state={rain} minutes={minutes} />
      <RadarBurstPanel
        // A new profile makes every analysis held stale: load them again.
        key={`bursts-${nodes.mac}-${learned}`}
        mac={nodes.mac}
        settings={settings.data}
        samples={samples}
        refresh={refresh}
        minutes={minutes}
        onMinutes={setMinutes}
        onNewest={setNewest}
        focus={focus}
        held={held}
        heldComplete={!analysis.thinning?.decimated}
      />
      <RadarPhase
        key={`phase-${nodes.mac}`}
        mac={nodes.mac}
        minutes={minutes}
      />
      <RadarCommissioning
        key={`profile-${nodes.mac}`}
        mac={nodes.mac}
        points={analysis.points}
        thinned={!!analysis.thinning?.decimated}
        available={!analysis.missing}
        minutes={minutes}
        refresh={refresh}
        onLearned={() => setLearned((n) => n + 1)}
      />
      <RadarStreamPanel
        key={`stream-${nodes.mac}`}
        mac={nodes.mac}
        settings={settings.data}
        refresh={refresh}
      />
      <section className="field-tools" aria-labelledby="radar-tools-title">
        <div className="field-section-head">
          <h2 id="radar-tools-title">Tools</h2>
          <p>
            Probe the radar itself: its echo profile, the blind-zone test and
            its reading on every wake.
          </p>
        </div>
        <RadarPanel
          key={`radar-${nodes.mac}`}
          mac={nodes.mac}
          samples={samples}
          settings={settings.data}
          refresh={refresh}
          onSent={() => setRefresh((n) => n + 1)}
        />
        <RadarHistory samples={samples} unit="mm" />
      </section>
      <RadarExplainers />
    </FieldLayout>
  );
}
